import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { UploadFileType } from 'src/app/model/Upload';
import { DataTableService } from 'src/app/service/data-table.service';
import { EventService } from 'src/app/service/event.service';
import { ExtVendorFileResponse, ExtVendorFileService } from 'src/app/service/ext-vendor-file.service';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilter, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { DragDropComponent, UploadFileRestrictions, UploadModalData } from '../dialogs/drag-drop/drag-drop.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserInfo } from 'src/app/model/User';
import { ModalConfig } from '../controls/modal/modal-config';
import { Utils } from 'src/app/utils';
import { ExternalVendorFileSettings } from 'src/app/model/Setting';
import { Events } from 'src/app/service/websocket.service';
import { AuthenticationService } from 'src/app/service/authentication.service';

export enum ExtVendorField {
  NAME = "name",
  FILENAME = "filename",
}

@Component({
    selector: 'app-settings-extvendor',
    templateUrl: './settings-extvendor.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-extvendor.component.css'],
    standalone: false
})
export class SettingsExtvendorComponent implements OnInit {

  cols: DataTableColumn[] = [];
  rows: DataTableRow[] = [];
  loading: boolean = false;
  loadingSettings: boolean = false;
  total: number = 0;
  fileResponse: ExtVendorFileResponse;
  solutionInfo: SolutionInfo = new SolutionInfo();
  pageInfo: PageEvent;
  filters: DataTableFilter[] = [];
  filterOpen: boolean = false;
  filterApplied: boolean = false;

  isSaving:boolean = false;
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;
  settings: ExternalVendorFileSettings;
  allowedExtList: any[] = [];
  isAuthorized: boolean = false;

  //filters
  queryValue: string = '';

  //subscription
  solutionInfoSubscription: Subscription;
  filterSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;

  settingsForm = new FormGroup({
    useRestrictionsCtrl: new FormControl(),
    maxFileCountCtrl: new FormControl(-1, [
      Validators.min(0),
      Validators.required]),
    maxFileSizeCtrl: new FormControl(-1, [
      Validators.min(0),
      Validators.required]),
    maxDiskSpaceCtrl: new FormControl(-1, [
      Validators.min(0),
      Validators.required]),
    allowedFileCtrl: new FormControl('', [
      Validators.pattern("^[a-z0-9]+$")
    ]),
  });

  constructor(private extSrv: ExtVendorFileService, private eventService: EventService,
    public router: Router, public route: ActivatedRoute, public modal: Modal, private dataTableSrv: DataTableService,
    private authSrv: AuthenticationService) {

    this.cols = Array<DataTableColumn>(
      new DataTableColumn("name", "Name", ColumnType.STRING, true, "24.5", ColumnSort.DESC),
      new DataTableColumn("filename", "File name", ColumnType.STRING, false, "24.5"),
      new DataTableColumn("type", "Type", ColumnType.STRING, false, "15"),
      new DataTableColumn("byteSize", "Size", ColumnType.FILESIZE, false, "12"),
      new DataTableColumn("uploadedBy", "Uploaded by", ColumnType.STRING, false, "12"),
      new DataTableColumn("dateTime", "Date", ColumnType.DATEFROMNOW, false, "12"),
    );
  
    this.filters = Array<DataTableFilter>(
      new DataTableFilter("ext-vendor-filter-event", ExtVendorField.NAME, $localize`Name or Filename`, FilterType.STRING, undefined)
    );


    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.getFiles(PageCount.TWENTYFIVE, 0);
    });

    this.filterSubscription = this.dataTableSrv.filterClicked$.subscribe(() => {
      this.filterOpen = !this.filterOpen;
      if (!this.filterOpen && this.filterApplied) {
        this.cancelFilter();
      }
    });

    this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
      this.dataTableSrv.onResetPagination();
      appliedFilters.forEach(appliedFilter => {
        this.queryValue = appliedFilter.selected;
      });
      this.getFiles(PageCount.TWENTYFIVE, 0);
      this.filterApplied = true;
    });

    this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
      this.cancelFilter();
    });
    this.currentUserAuthInfo = this.authSrv.getLoggedInUser();

    this.isAuthorized = (Utils.isSuperuserRole(this.currentUserAuthInfo) || Utils.isAdminRole(this.currentUserAuthInfo));
    this.loadingSettings = true;    
    this.setSettingsAccess();
  }

  get useRestrictionsCtrl() { return this.settingsForm.get('useRestrictionsCtrl'); }
  get maxFileCountCtrl() { return this.settingsForm.get('maxFileCountCtrl'); }
  get maxFileSizeCtrl() { return this.settingsForm.get('maxFileSizeCtrl'); }
  get maxDiskSpaceCtrl() { return this.settingsForm.get('maxDiskSpaceCtrl'); }
  get allowedFileCtrl() { return this.settingsForm.get('allowedFileCtrl'); }

  ngOnInit(): void {
    this.extSrv.getExternalVendorFileSettings().pipe(
      tap(resp => {
        this.settings = resp.body;
        this.initData();
      }),
      finalize(() => {
        this.loadingSettings = false;
        this.setSettingsAccess();
      })
    ).subscribe();
  }

  initData(): void {
    this.useRestrictionsCtrl.setValue(this.settings.useRestrictions);
    this.maxFileCountCtrl.setValue(this.settings.maxFileCount == -1 ? this.settings.defaultMaxFileCount : this.settings.maxFileCount);
    this.maxFileSizeCtrl.setValue(this.settings.maxFileSize == -1 ? this.settings.defaultMaxFileSize : this.settings.maxFileSize);
    this.maxDiskSpaceCtrl.setValue(this.settings.maxDiskSpace == -1 ? this.settings.defaultMaxDiskSpace : this.settings.maxDiskSpace);
    this.allowedExtList = Object.assign([], this.settings.allowedExtensions);
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageInfo = pageEvent;
    this.getFiles(pageEvent.itemsPerPage, pageEvent.startingIndex);
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    switch (event.name) {
      case 'Delete':
        this.canDeleteFiles(event.data);
        break;
    }
  }

  getFiles(pageSize: number, startIndex: number) {
    this.loading = true;
    var tmpRows: DataTableRow[] = [];
    this.extSrv.getFiles(pageSize, startIndex, this.queryValue).pipe(
      tap(resp => {
        this.fileResponse = resp.body;
        this.total = this.fileResponse.count;
        this.fileResponse.files.forEach(file => {
          var actions = new Array<ActionMenuItem>(
            new ActionMenuItem("Delete", $localize`Delete`, ActionMenuIcon.DELETE, file, true)
          );
          tmpRows.push(new DataTableRow(file, actions));
        });
        this.rows = tmpRows;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  onClickDragDrop(): void {
    let showDialog: boolean = true;
    if(this.settings.useRestrictions){
       if(this.rows.length + 1 > this.settings.maxFileCount){
        this.showFileCountError((this.rows.length + 1) - this.settings.maxFileCount);
        showDialog = false;;
       }
    } 

    if(showDialog){
      let fileRestrictions: UploadFileRestrictions = new UploadFileRestrictions();
      fileRestrictions.useRestrictions = this.settings.useRestrictions;
      fileRestrictions.maxFileSize = this.settings.maxFileSize;
      fileRestrictions.maxDiskSpace = this.settings.maxDiskSpace;
      fileRestrictions.usedDiskSpace = this.getUsedDiskSpace();
      fileRestrictions.allowedExtensions = this.settings.allowedExtensions != undefined ? this.settings.allowedExtensions : [];
      fileRestrictions.currentFileTotal = this.total;
      fileRestrictions.maxFileCount = this.settings.maxFileCount;
      let modalData: UploadModalData =  new UploadModalData(UploadFileType.EXTERNALVENDOR, false, undefined, undefined, undefined, false,
        fileRestrictions);
      modalData.supportMultiFiles = true;
      const sub: Subscription =
        this.modal.open(DragDropComponent, {
          data: modalData,
          id: 'otsc-drag-drop-dialog',
          customclass: 'ot-modal-side-panel ot-modal-side-panel-md'
        }).afterClosed.subscribe((result) => {
          if (result) {
            this.getFiles(PageCount.TWENTYFIVE, 0);
          }
          sub.unsubscribe();
        });
    }
  }

  canDeleteFiles(data: any[]): void{
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Delete`,
          type: ModalMessageType.Question,
          message: data.length === 1 ? $localize`The file ${data[0].filename} will be permanently removed. Remove the file?` :
            $localize`This action will permanently remove the ${data.length} files. Would you like to continue?`,
          primaryBtn: $localize`Delete`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-delete-ext-files-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.deleteFiles(data);
        }
        sub.unsubscribe();
      });
  }

  deleteFiles(files: any[]): void {
    let deleteList: string[] = [];

    files.forEach(file => {
        deleteList.push(file.name);
    });

    if(deleteList.length > 0){
      this.extSrv.deleteFiles(deleteList).pipe(
        tap(resp => {
          this.getFiles(PageCount.TWENTYFIVE, 0);
        })
      ).subscribe();
    }
  }

  cancelFilter(): void {
    this.queryValue = '';
    this.getFiles(PageCount.TWENTYFIVE, 0);
    this.filterApplied = false;
  }

  save(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-general-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;
          this.settings.useRestrictions = this.useRestrictionsCtrl.value;
          this.settings.maxDiskSpace = this.maxDiskSpaceCtrl.value;
          this.settings.maxFileCount = this.maxFileCountCtrl.value;
          this.settings.maxFileSize = this.maxFileSizeCtrl.value;
          this.settings.allowedExtensions = this.allowedExtList;

          this.extSrv.updateExternalVendorFileSettings(this.settings).pipe(tap(resp => {
            if (resp.body) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
            } else {
              this.settingsForm.reset(this.settingsForm.value);
              Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
            }
          }),
            finalize(() => {
              this.settingsForm.markAsPristine();
              this.isSaving = false;
              this.savingBtn = $localize`Save`;
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-general-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
          this.settingsForm.markAsPristine();
        }
        sub.unsubscribe();
      });
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-general-unsaved-changes');
  }

  addToAllowedList(): void {
    if(this.allowedFileCtrl.value != null && this.allowedFileCtrl.value != '' && this.allowedFileCtrl.valid){
      if(this.allowedExtList.indexOf(this.allowedFileCtrl.value) > -1){
        this.modal.open(ModalMessageComponent, {
          data: {
                  title: $localize`Error`,
                  message: $localize`'${this.allowedFileCtrl.value}' is already added to the allowed extensions.`,
                  primaryBtn: $localize`OK`,
                  type: ModalMessageType.Error,
                  width: '350px',
                },
          id: 'otsc-settings-proxy-invalid-exception'
          }
        );
      }else{
        this.allowedExtList.push(this.allowedFileCtrl.value);
        this.allowedFileCtrl.setValue('');
      }
    }
  }

  removeFromAllowedList(index: number): void {
    this.allowedExtList.splice(index, 1)
    this.settingsForm.markAsDirty();
  }

  showFileCountError(count: number): void {
    this.modal.open(ModalMessageComponent, {
      data: {
              title: $localize`Error`,
              message: $localize`New files cannot be added at this time, the maximum file count has been reached.  To add a new file ${count} must be deleted.`,
              primaryBtn: $localize`OK`,
              type: ModalMessageType.Error,
              width: '350px',
            },
      id: 'otsc-settings-file-count-eror'
      }
    );
  }

  getUsedDiskSpace(): number { 
    let usedSpace: number = 0;
    this.rows.forEach(row => {
      usedSpace += row.data.byteSize;
    });
    return usedSpace;
  }

  setSettingsAccess(): void {
    if(this.isAuthorized && !this.loadingSettings){
      this.useRestrictionsCtrl.enable();
      this.allowedFileCtrl.enable();
      this.maxFileSizeCtrl.enable();
      this.maxFileCountCtrl.enable();
      this.maxDiskSpaceCtrl.enable();
    } else {
      this.useRestrictionsCtrl.disable();
      this.allowedFileCtrl.disable();
      this.maxFileSizeCtrl.disable();
      this.maxFileCountCtrl.disable();
      this.maxDiskSpaceCtrl.disable();
    }
  }

}
